/**
 * Analytics Tracking Utilities
 *
 * Centralized event tracking for the V2 application.
 * Uses PostHog under the hood.
 *
 * Usage:
 *   import { Analytics } from '../utils/analytics';
 *   Analytics.track('channel_selected', { channel_id: 'abc123' });
 */

import { trackEvent } from '../../config/posthog.config';
import { logger } from '../../utils/logger';

/**
 * Standard event names for consistency
 */
export enum AnalyticsEvent {
  // Navigation
  PAGE_VIEW = 'page_view',
  TAB_CHANGED = 'tab_changed',

  // Channel events
  CHANNEL_SELECTED = 'channel_selected',
  CHANNEL_DESELECTED = 'channel_deselected',
  CHANNEL_CREATED = 'channel_created',
  CHANNEL_UPDATED = 'channel_updated',

  // Message events
  MESSAGE_PLAYED = 'message_played',
  MESSAGE_VIEWED = 'message_viewed',
  MESSAGE_SEARCHED = 'message_searched',

  // Alert events
  ALERT_CREATED = 'alert_created',
  ALERT_UPDATED = 'alert_updated',
  ALERT_DELETED = 'alert_deleted',
  ALERT_TRIGGERED = 'alert_triggered',

  // Live Feed events
  LIVE_FEED_STARTED = 'live_feed_started',
  LIVE_FEED_STOPPED = 'live_feed_stopped',
  LIVE_FEED_CHANNEL_ADDED = 'live_feed_channel_added',
  LIVE_FEED_CHANNEL_REMOVED = 'live_feed_channel_removed',

  // KODI events
  KODI_QUERY_SENT = 'kodi_query_sent',
  KODI_RESPONSE_RECEIVED = 'kodi_response_received',

  // Settings events
  SETTINGS_UPDATED = 'settings_updated',
  USER_INVITED = 'user_invited',
  SPEAKER_CREATED = 'speaker_created',
  TAG_CREATED = 'tag_created',

  // Auth events
  USER_SIGNED_IN = 'user_signed_in',
  USER_SIGNED_OUT = 'user_signed_out',
  USER_SIGNED_UP = 'user_signed_up',

  // Organization events
  ORG_SWITCHED = 'organization_switched',

  // Feature usage
  FEATURE_USED = 'feature_used',
  EXPORT_CREATED = 'export_created'
}

/**
 * Analytics utility class
 */
export class Analytics {
  /**
   * Track a custom event
   */
  static track(event: AnalyticsEvent | string, properties?: Record<string, any>): void {
    try {
      trackEvent(event, properties);
      logger.debug('[Analytics] Event tracked:', event, properties);
    } catch (error) {
      logger.error('[Analytics] Failed to track event:', error);
    }
  }

  /**
   * Track a page view
   */
  static pageView(pageName: string, properties?: Record<string, any>): void {
    Analytics.track(AnalyticsEvent.PAGE_VIEW, {
      page: pageName,
      ...properties
    });
  }

  /**
   * Track channel selection
   */
  static channelSelected(channelId: string, channelName?: string): void {
    Analytics.track(AnalyticsEvent.CHANNEL_SELECTED, {
      channel_id: channelId,
      channel_name: channelName
    });
  }

  /**
   * Track message playback
   */
  static messagePlayed(talkId: string, channelId: string, duration?: number): void {
    Analytics.track(AnalyticsEvent.MESSAGE_PLAYED, {
      talk_id: talkId,
      channel_id: channelId,
      duration
    });
  }

  /**
   * Track alert creation
   */
  static alertCreated(alertId: string, terms: string[], channels: string[]): void {
    Analytics.track(AnalyticsEvent.ALERT_CREATED, {
      alert_id: alertId,
      term_count: terms.length,
      channel_count: channels.length
    });
  }

  /**
   * Track live feed start
   */
  static liveFeedStarted(channelIds: string[]): void {
    Analytics.track(AnalyticsEvent.LIVE_FEED_STARTED, {
      channel_count: channelIds.length
    });
  }

  /**
   * Track live feed stop
   */
  static liveFeedStopped(duration?: number): void {
    Analytics.track(AnalyticsEvent.LIVE_FEED_STOPPED, {
      duration
    });
  }

  /**
   * Track KODI query
   */
  static kodiQuery(queryType: string, promptLength: number): void {
    Analytics.track(AnalyticsEvent.KODI_QUERY_SENT, {
      query_type: queryType,
      prompt_length: promptLength
    });
  }

  /**
   * Track organization switch
   */
  static orgSwitched(fromOrgId: string, toOrgId: string): void {
    Analytics.track(AnalyticsEvent.ORG_SWITCHED, {
      from_org_id: fromOrgId,
      to_org_id: toOrgId
    });
  }

  /**
   * Track search
   */
  static search(query: string, resultCount: number): void {
    Analytics.track(AnalyticsEvent.MESSAGE_SEARCHED, {
      query_length: query.length,
      result_count: resultCount
    });
  }

  /**
   * Track feature usage (generic)
   */
  static featureUsed(featureName: string, properties?: Record<string, any>): void {
    Analytics.track(AnalyticsEvent.FEATURE_USED, {
      feature: featureName,
      ...properties
    });
  }
}

export default Analytics;
